import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useSearchParams } from "react-router-dom";
import { useSubscription } from "@/context/SubscriptionContext";
import { useSchool } from "@/context/SchoolContext";
import { toast } from "sonner";
import { createCreditCheckoutSession } from "@/utils/createCheckoutSession";
import { PageHeader } from "@/layouts/PageHeader";
import { PageContainer } from "@/layouts/PageContainer";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Sparkles, Building2, Plus } from "lucide-react";
import { Modal, ModalBody } from "@/components/modals/Modal";
import { CreditPackCard } from "@/components/entities/CreditPackCard";
import { REPORT_PACKS, AI_PACKS } from "@/constants/creditPacks";

// Per-type wallet panel: balance + its own Buy button. Each credit type is
// fully self-contained so reports and AI never blur together.
function WalletCard({ Icon, label, value, hint, onBuy }) {
  return (
    <div className="border border-border rounded-xl bg-card p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon size={18} className="text-primary" />
          </div>
          <span className="text-base font-semibold text-foreground">{label}</span>
        </div>
        <Button size="sm" variant="outline" onClick={onBuy}>
          <Plus size={15} /> Buy
        </Button>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-semibold text-foreground tabular-nums">
          {value ?? "-"}
        </span>
        <span className="text-base text-muted-foreground">remaining</span>
      </div>
      {hint && <p className="text-sm text-muted-foreground mt-2">{hint}</p>}
    </div>
  );
}

// Per-type config for the buy dialog - reuses the exact Pricing page cards.
const BUY_CONFIG = {
  reports: {
    title: "Buy Report Credits",
    Icon: FileText,
    packs: REPORT_PACKS,
    unit: "report",
    blurb: "Each report credit covers one student report you create.",
  },
  ai: {
    title: "Buy AI Credits",
    Icon: Sparkles,
    packs: AI_PACKS,
    unit: "comment",
    hideUnit: true,
    blurb: "Each AI credit generates one AI-written comment.",
  },
};

// Dialog showing only one credit type's packs at a time.
function BuyCreditsModal({ type, onClose, onBuy, loading }) {
  const config = type ? BUY_CONFIG[type] : null;

  return (
    <Modal
      open={!!config}
      onClose={onClose}
      title={config?.title}
      subtitle="Added to your wallet instantly · never expire"
      icon={config ? <config.Icon size={16} className="text-primary" /> : null}
      maxWidth="max-w-2xl"
    >
      {config && (
        <ModalBody className="space-y-4">
          <p className="text-sm text-muted-foreground">{config.blurb}</p>
          <div className="grid grid-cols-4 gap-3 max-[560px]:grid-cols-2">
            {config.packs.map((p) => (
              <CreditPackCard
                key={p.id}
                pack={p}
                unit={config.unit}
                hideUnit={config.hideUnit}
                onBuy={onBuy}
                loading={loading}
              />
            ))}
          </div>
        </ModalBody>
      )}
    </Modal>
  );
}

export default function Usage() {
  const { getToken } = useAuth();
  const { reportsRemaining, aiCreditsRemaining, seatInfo, loading, error, refetch } = useSubscription();
  const { memberships, isAdmin } = useSchool();
  const [buyingPack, setBuyingPack] = useState(null);
  const [buyType, setBuyType] = useState(null); // "reports" | "ai" | null
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("success") === "true") {
      setSearchParams({}, { replace: true });
      // Small delay to give the webhook time to process before refetching
      const t = setTimeout(() => {
        refetch();
        toast.success("Credits added to your wallet!");
      }, 2000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuy = async (packId) => {
    setBuyingPack(packId);
    try {
      const token = await getToken();
      await createCreditCheckoutSession(packId, token);
    } finally {
      setBuyingPack(null);
    }
  };

  const statusVariant =
    seatInfo?.status === "active" ? "success"
    : seatInfo?.status === "past_due" ? "warning"
    : "default";

  const statusLabel =
    seatInfo?.status === "active" ? "Active"
    : seatInfo?.status === "past_due" ? "Past Due"
    : seatInfo?.status === "cancelled" ? "Cancelled"
    : seatInfo?.status ?? "Unknown";

  const reportsUsedPct = seatInfo
    ? Math.min(100, Math.round((seatInfo.reportsUsed / seatInfo.reportsMax) * 100))
    : 0;
  const aiUsedPct = seatInfo
    ? Math.min(100, Math.round((seatInfo.aiGensUsed / seatInfo.aiMax) * 100))
    : 0;

  const schoolName = memberships.length === 1 ? memberships[0].name : null;

  return (
    <PageContainer>
      <PageHeader
        title="Credits & Billing"
        subtitle="Your quota, wallet balance, and credit purchases."
      />

      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Failed to load subscription info.</p>}

      {!loading && !error && (
        <div className="space-y-10">

          {/* School Plan */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
              School Plan
            </h2>

            {seatInfo !== null ? (
              <div className="border border-border rounded-xl p-5 bg-card space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={statusVariant}>{statusLabel}</Badge>
                    {schoolName && (
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Building2 size={13} />
                        {schoolName}
                      </span>
                    )}
                  </div>
                  {seatInfo.periodEnd && (
                    <span className="text-xs text-muted-foreground">
                      Renews{" "}
                      {new Date(seatInfo.periodEnd).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span>Reports Used</span>
                    <span
                      className={
                        seatInfo.reportsUsed >= seatInfo.reportsMax
                          ? "text-destructive font-semibold"
                          : ""
                      }
                    >
                      {seatInfo.reportsUsed} / {seatInfo.reportsMax}
                    </span>
                  </div>
                  <Progress value={reportsUsedPct} max={100} />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span>AI Generations Used</span>
                    <span
                      className={
                        seatInfo.aiGensUsed >= seatInfo.aiMax
                          ? "text-destructive font-semibold"
                          : ""
                      }
                    >
                      {seatInfo.aiGensUsed} / {seatInfo.aiMax}
                    </span>
                  </div>
                  <Progress value={aiUsedPct} max={100} />
                </div>

                {isAdmin ? (
                  <p className="text-xs text-muted-foreground">
                    You&apos;re the school admin.{" "}
                    <Link to="/schools/seats" className="underline hover:text-foreground">
                      Manage seat billing
                    </Link>
                    .
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Managed by your school administrator.
                  </p>
                )}
              </div>
            ) : (
              <div className="border border-border rounded-xl p-5 bg-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                    <Building2 size={16} className="text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">No school plan</p>
                    <p className="text-sm text-muted-foreground">
                      School Plans cover 120 report credits and 200 AI credits per year. Your personal wallet below is always available if you need more.
                    </p>
                    {memberships.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        You are in a school but don&apos;t have a school plan assigned to you. Ask your admin.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Your Wallet */}
          <div>
            <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-1.5">
              Your Wallet
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              {seatInfo !== null
                ? "Drawn on only after your school plan allocation runs out. Credits never expire."
                : "Two separate balances - report credits and AI credits. Both never expire."}
            </p>
            <div className="grid grid-cols-2 gap-4 max-[640px]:grid-cols-1">
              <WalletCard
                Icon={FileText}
                label="Report Credits"
                value={reportsRemaining}
                hint="Used when you create a student report."
                onBuy={() => setBuyType("reports")}
              />
              <WalletCard
                Icon={Sparkles}
                label="AI Credits"
                value={aiCreditsRemaining}
                hint="Used when you generate an AI comment."
                onBuy={() => setBuyType("ai")}
              />
            </div>
          </div>

        </div>
      )}

      <BuyCreditsModal
        type={buyType}
        onClose={() => setBuyType(null)}
        onBuy={(pack) => handleBuy(pack.id)}
        loading={buyingPack}
      />
    </PageContainer>
  );
}
